import { useListInitiativeAppIntegrationsQuery } from '@api/initiative-app-integrations';
import Dashboard, { DashboardRow, DashboardBorderless, DashboardSectionTitle } from '@g17eco/molecules/dashboard';
import { Loader } from '@g17eco/atoms/loader';
import { JSX } from 'react';
import {
  IntegrationProvider,
  IntegrationProviderExtended,
  ProviderIntegrationStatus,
} from '@g17eco/types/app-integration';
import { BasicAlert } from '@g17eco/molecules/alert';
import { AdminBreadcrumbsProps } from '@g17eco/molecules/breadcrumbs';
import { IntegrationList } from './IntegrationList';
import { IntegrationModal } from './IntegrationModal';
import { IntegrationProviderCard, type IntegrationProviderCardAction } from './IntegrationProviderCard';
import { useIntegrationModal } from '@features/integrations/hooks/useIntegrationModal';
import { useHistory } from 'react-router-dom';
import { ROUTES } from '@constants/routes';
import { generateUrl } from '@routes/util';
import EmissionsTrackerAppLogo from '@g17eco/images/apps/Emissions_Tracker_logo.svg';

const EMISSIONS_CALCULATOR_CODES = new Set(['emissions-tracker','green-project-tech', 'greenly', 'unravel-carbon']);

const emissionsCalculatorBranding = (provider: IntegrationProvider) => {
  if (!EMISSIONS_CALCULATOR_CODES.has(provider.code)) {
    return {};
  }
  return {
    icon: EmissionsTrackerAppLogo as string,
    poweredByLogo: provider.logoFooter || provider.logo,
  };
};

export interface CrossLinkCard {
  key: string;
  logo: string;
  color?: string;
  name: string;
  description: string;
  learnMoreUrl?: string;
  /** Render as a connected card (green pill + Access button) or ready-to-set-up (blue pill + Set up). */
  section: 'connected' | 'available';
  subtitle?: string;
  onOpen: React.MouseEventHandler<HTMLElement> | (() => void);
  openLabel?: string;
  manageActions?: IntegrationProviderCardAction[];
}

interface Props {
  initiativeId: string;
  /** App-level cross-links (e.g. Company Tracker, Materiality Tracker) rendered alongside real integrations. */
  crossLinkCards?: CrossLinkCard[];
  BreadCrumbsComponent: (props: AdminBreadcrumbsProps) => JSX.Element;
}

const connectionSubtitle = (provider: IntegrationProviderExtended): string | undefined => {
  if (provider.status !== ProviderIntegrationStatus.Active) {
    return undefined;
  }
  // Backend does not yet expose a lastUpdated timestamp on usedProviders; leave undefined when absent.
  return undefined;
};

export const IntegrationsDashboard = ({ initiativeId, crossLinkCards = [], BreadCrumbsComponent }: Props) => {
  const history = useHistory();
  const { code, setCode, onLinkClick } = useIntegrationModal(initiativeId);
  const goToSetup = (providerCode: string) =>
    history.push(generateUrl(ROUTES.APP_INTEGRATIONS_VIEW, { initiativeId, code: providerCode }));

  const { data, isLoading, error } = useListInitiativeAppIntegrationsQuery(initiativeId);

  const { allProviders = [], usedProviders = [] } = data ?? {};

  if (isLoading) {
    return <Loader />;
  }

  const usedCodes = new Set(usedProviders.map((p) => p.code));
  const connectedProviders = usedProviders;
  const availableProviders: IntegrationProvider[] = allProviders.filter((p) => !usedCodes.has(p.code));

  const connectedCrossLinks = crossLinkCards.filter((c) => c.section === 'connected');
  const availableCrossLinks = crossLinkCards.filter((c) => c.section === 'available');

  const connectedCount = connectedProviders.length + connectedCrossLinks.length;
  const availableCount = availableProviders.length + availableCrossLinks.length;

  const renderConnectedProvider = (provider: IntegrationProviderExtended) => {
    const actions: IntegrationProviderCardAction[] = [{ label: 'Details', onClick: () => setCode(provider.code) }];
    const accessUrl = provider.login?.url;
    return (
      <div key={provider.code} className='col-12 col-md-6 col-lg-4'>
        <IntegrationProviderCard
          logo={provider.logo}
          name={provider.name}
          active={true}
          description={provider.description}
          learnMoreUrl={provider.link}
          status={{
            kind: 'connected',
            subtitle: connectionSubtitle(provider),
            onAccess: accessUrl ? () => onLinkClick({ link: accessUrl, code: provider.code }) : undefined,
          }}
          actions={actions}
          {...emissionsCalculatorBranding(provider)}
        />
      </div>
    );
  };

  const renderAvailableProvider = (provider: IntegrationProvider) => {
    return (
      <div key={provider.code} className='col-12 col-md-6 col-lg-4'>
        <IntegrationProviderCard
          logo={provider.logo}
          name={provider.name}
          description={provider.description}
          learnMoreUrl={provider.link}
          status={provider.enabled ? { kind: 'ready', onSetUp: () => goToSetup(provider.code) } : { kind: 'disabled' }}
          {...emissionsCalculatorBranding(provider)}
        />
      </div>
    );
  };

  const renderCrossLink = (card: CrossLinkCard) => {
    const handleOpen = () => {
      (card.onOpen as (event?: React.MouseEvent<HTMLElement>) => void)();
    };
    return (
      <div key={card.key} className='col-12 col-md-6 col-lg-4'>
        <IntegrationProviderCard
          logo={card.logo}
          active={card.section === 'connected'}
          name={card.name}
          description={card.description}
          learnMoreUrl={card.learnMoreUrl}
          status={
            card.section === 'connected'
              ? { kind: 'connected', subtitle: card.subtitle, onAccess: handleOpen }
              : { kind: 'ready', onSetUp: handleOpen }
          }
          actions={card.manageActions}
        />
      </div>
    );
  };

  return (
    <Dashboard>
      <DashboardRow>
        <BreadCrumbsComponent initiativeId={initiativeId} breadcrumbs={[{ label: 'App integrations' }]} />
      </DashboardRow>
      <DashboardSectionTitle title='App integrations' subtitle='Connect external tools to pull their data into your disclosures.' />
      <DashboardBorderless>
        {error ? <BasicAlert type='danger'>{'message' in error ? error.message : String(error)}</BasicAlert> : null}

        <IntegrationList title='Connected' count={connectedCount}>
          {connectedCrossLinks.map(renderCrossLink)}
          {connectedProviders.map(renderConnectedProvider)}
        </IntegrationList>

        <IntegrationList title='Available' count={availableCount}>
          {availableCrossLinks.map(renderCrossLink)}
          {availableProviders.map(renderAvailableProvider)}
        </IntegrationList>

        <IntegrationModal
          code={code}
          initiativeId={initiativeId}
          closeModal={() => setCode(undefined)}
          handleLinkClick={onLinkClick}
        />
      </DashboardBorderless>
    </Dashboard>
  );
};
